/**
 * Rules that stop a staged product from reaching the catalog — either the
 * promote API itself, or auto-promotion while a human could still override.
 *
 * Keep gap copy aligned with `evaluateStagedApprovalReadiness` and
 * `evaluateAutoPromoteEligibility` on the API.
 */

import { identifierReviewIssueMessages } from './identifierReviewNotes';
import { catalogContentConsistencyIssues } from './catalogContentConsistency';
import { getStagedApprovalGaps, type StagedApprovalGapId } from './stagedApprovalGaps';
import { clampShortDescription } from './shortDescription';
import { qualifiesForPreReleaseUpcPending, type PreReleaseRowLike } from './preReleaseUpcPending';

export type PromoteBlockerId =
  | StagedApprovalGapId
  | 'aiContentPending'
  | 'inTheBoxEffort'
  | 'duplicateUpc'
  | 'kitMismatch'
  | 'upcMismatch'
  | 'upcPendingPreRelease';

export interface PromoteBlocker {
  id: PromoteBlockerId;
  label: string;
  message: string;
  /** `POST /promote` (and approve+autoPromote) will 400. */
  blocksPromote: boolean;
  /** Scheduled auto-promotion will hold this row. */
  blocksAutoPromote: boolean;
}

export function overlayMergePreviewBarcodes<T extends object>(
  product: T,
  preview?: {
    mergedIdentifiers?: { upc?: string; ean?: string; gtin?: string };
    fields?: Array<{ path: string; after?: string }>;
  } | null,
): T {
  if (!preview) return product;
  const row = product as T & { upc?: string; ean?: string; gtin?: string };
  const after = (path: string) => {
    const v = preview.fields?.find((f) => f.path === path)?.after;
    if (!v || v === '(removed)') return undefined;
    return v;
  };
  const upc = String(row.upc || '').trim() || preview.mergedIdentifiers?.upc || after('upc');
  const ean = String(row.ean || '').trim() || preview.mergedIdentifiers?.ean || after('ean');
  const gtin = String(row.gtin || '').trim() || preview.mergedIdentifiers?.gtin || after('gtin');
  if (
    upc === String(row.upc || '').trim() &&
    ean === String(row.ean || '').trim() &&
    gtin === String(row.gtin || '').trim()
  ) {
    return product;
  }
  return {
    ...product,
    ...(upc ? { upc } : {}),
    ...(ean ? { ean } : {}),
    ...(gtin ? { gtin } : {}),
  };
}

type StagedLike = Parameters<typeof getStagedApprovalGaps>[0] & PreReleaseRowLike & {
  description?: string;
  aiDescription?: string;
  shortDescription?: string;
  aiShortDescription?: string;
  features?: unknown;
  aiFeatures?: unknown;
  inTheBox?: unknown;
  remediation?: {
    /** Camera, lens or kit (server `isInTheBoxCritical`); missing means it holds. */
    inTheBoxCritical?: boolean;
    gaps?: Partial<
      Record<
        string,
        {
          exhausted?: boolean;
          resolvedAt?: unknown;
          attemptCounts?: Record<string, number>;
        }
      >
    >;
  };
};

function hasNamedInTheBox(value: unknown): boolean {
  if (!Array.isArray(value)) return false;
  return value.some((v) => {
    if (typeof v === 'string') return v.trim().length > 0;
    const row = v as { name?: unknown; item?: unknown } | null;
    return String(row?.name || row?.item || '').trim().length > 0;
  });
}

/**
 * Keep in sync with `inTheBoxEffortPending` in src/services/stagedRemediation.ts.
 * For a camera, lens or kit, auto-approve waits until the box fill has been
 * tried (or the box is already filled / exhausted); other products fill it
 * after promotion. A human promote is allowed either way.
 */
export function inTheBoxEffortPending(product: StagedLike): boolean {
  if (product.remediation?.inTheBoxCritical === false) return false;
  if (hasNamedInTheBox(product.inTheBox)) return false;
  const state = product.remediation?.gaps?.inTheBox;
  if (state?.exhausted || state?.resolvedAt) return false;
  if ((state?.attemptCounts?.bh_fill || 0) > 0) return false;
  return true;
}

const GAP_COPY: Record<StagedApprovalGapId, { label: string; message: string }> = {
  sku: { label: 'SKU', message: 'SKU is required' },
  upc: { label: 'UPC/EAN', message: 'UPC or EAN is required' },
  category: { label: 'Category', message: 'Product category is required' },
  productType: { label: 'Product type', message: 'Product type is required' },
  price: { label: 'Price', message: 'A positive price (MSRP, MAP, retail, or sale) is required' },
  weight: { label: 'Weight', message: 'Product weight is required' },
  photo: { label: 'Photo', message: 'At least one product image is required' },
  shortDescription: { label: 'Short description', message: 'Short description is required' },
  longDescription: { label: 'Description', message: 'Long description must be more than 3 sentences' },
  features: { label: 'Features', message: 'More than 3 features are required' },
  fujifilmCatalog: { label: 'Fujifilm catalog', message: 'Fujifilm catalog code is invalid' },
  identifierReview: {
    label: 'Identifier review',
    message: 'An identifier looks odd or invalid — edit it or mark Looks OK before approve',
  },
  verificationError: {
    label: 'Verification error',
    message: 'AI verification found a critical identity or data error that must be resolved',
  },
  contentConsistency: {
    label: 'Wrong product content',
    message: 'The description, specs or In The Box name a different product than this listing',
  },
};

export type PendingAiCatalogField = 'description' | 'shortDescription' | 'features';

/**
 * Catalog fields that still hold an unaccepted AI draft — same rule as
 * `pendingAiContentFields` on the API (the `aiContentPending` remediation gap).
 */
export function pendingAiCatalogFields(product: StagedLike): PendingAiCatalogField[] {
  const pending: PendingAiCatalogField[] = [];
  const txt = (v: unknown) => String(v || '').trim();
  if (txt(product.aiDescription) && txt(product.aiDescription) !== txt(product.description)) {
    pending.push('description');
  }
  // Accepting an over-length AI short clamps it to the catalog budget, so a
  // catalog value equal to the clamped draft counts as accepted (mirrors
  // `pendingAiContentFields` on the API).
  const aiShort = txt(product.aiShortDescription);
  const catalogShort = txt(product.shortDescription);
  if (aiShort && aiShort !== catalogShort && clampShortDescription(aiShort) !== catalogShort) {
    pending.push('shortDescription');
  }
  const aiFeatures = Array.isArray(product.aiFeatures) ? product.aiFeatures : [];
  const features = Array.isArray(product.features) ? product.features : [];
  if (aiFeatures.length > 0 && JSON.stringify(aiFeatures) !== JSON.stringify(features)) {
    pending.push('features');
  }
  return pending;
}

export function getPromoteBlockers(
  product: StagedLike,
  options?: {
    duplicateUpc?: boolean;
    kitMismatch?: boolean;
    upcMismatch?: boolean;
    /** Flag `prerelease_upc_pending` (MOT-651): a pre-release row may promote without a barcode. */
    preReleaseUpcPending?: boolean;
  },
): PromoteBlocker[] {
  const blockers: PromoteBlocker[] = [];

  for (const gap of getStagedApprovalGaps(product)) {
    // Same waiver as `evaluateStagedApprovalReadiness` with the flag on: the
    // barcode gap stays visible but no longer blocks a human promote. Auto-promotion
    // still holds it, so a person decides when the pre-order product goes out.
    if (gap === 'upc' && options?.preReleaseUpcPending && qualifiesForPreReleaseUpcPending(product)) {
      blockers.push({
        id: 'upcPendingPreRelease',
        label: PRE_RELEASE_UPC_PENDING_LABEL,
        message:
          'Pre-release product with no UPC or EAN yet. You can promote it; stores that carry the brand are told ' +
          "it has no UPC yet, the lookup keeps running, and they are told again when it arrives. Auto-approve won't promote it.",
        blocksPromote: false,
        blocksAutoPromote: true,
      });
      continue;
    }
    const copy = GAP_COPY[gap];
    const message =
      gap === 'identifierReview'
        ? identifierReviewIssueMessages(product).join('; ') || copy.message
        : gap === 'contentConsistency'
          ? catalogContentConsistencyIssues(product).map((issue) => issue.message).join('; ') || copy.message
          : copy.message;
    blockers.push({
      id: gap,
      label: copy.label,
      message,
      blocksPromote: true,
      blocksAutoPromote: true,
    });
  }

  if (inTheBoxEffortPending(product)) {
    blockers.push({
      id: 'inTheBoxEffort',
      label: 'In The Box',
      message:
        'In The Box fill has not been tried yet. For a camera, lens or kit, auto-approve waits until the stored listing is parsed and the B&H fill has run (or the gap is marked exhausted). You can still promote by hand.',
      blocksPromote: false,
      blocksAutoPromote: true,
    });
  }

  const pendingAi = pendingAiCatalogFields(product);
  if (pendingAi.length > 0) {
    const fields = pendingAi.join(', ');
    blockers.push({
      id: 'aiContentPending',
      label: 'AI content unaccepted',
      message:
        `AI ${fields} ${pendingAi.length === 1 ? 'is' : 'are'} not copied into the catalog fields. ` +
        'Auto-promote is blocked until you accept them.',
      blocksPromote: false,
      blocksAutoPromote: true,
    });
  }

  if (options?.duplicateUpc) {
    blockers.push({
      id: 'duplicateUpc',
      label: 'Duplicate UPC',
      message: 'This UPC collides with another active product — promote will fail until resolved',
      blocksPromote: true,
      blocksAutoPromote: true,
    });
  }

  if (options?.kitMismatch) {
    blockers.push({
      id: 'kitMismatch',
      label: 'Kit vs body',
      message:
        'This scrape is a lens kit or a camera body, and the linked catalog row is the other. They are different products — do not merge. Fix the SKU or reject the row.',
      blocksPromote: true,
      blocksAutoPromote: true,
    });
  }

  if (options?.upcMismatch) {
    blockers.push({
      id: 'upcMismatch',
      label: 'UPC would change',
      message:
        'SKU matches the catalog product but UPC/EAN would change. That usually means a different item. Clear the staged barcode to keep the catalog UPC, or reject the row.',
      blocksPromote: true,
      blocksAutoPromote: true,
    });
  }

  return blockers;
}

export function promoteWillFail(blockers: PromoteBlocker[]): boolean {
  return blockers.some((b) => b.blocksPromote);
}

export const PRE_RELEASE_UPC_PENDING_LABEL = 'Pre-release, UPC pending';

/** The one chip label for a row with blockers: hard, pre-release waiver, or held. */
export function promoteStateLabel(blockers: PromoteBlocker[]): string {
  if (promoteWillFail(blockers)) return 'Cannot promote';
  if (blockers.some((b) => b.id === 'upcPendingPreRelease')) return PRE_RELEASE_UPC_PENDING_LABEL;
  return 'Auto-approve held';
}

export function summarizePromoteBlockers(blockers: PromoteBlocker[]): string {
  if (blockers.length === 0) return '';
  const hard = blockers.filter((b) => b.blocksPromote);
  if (hard.length > 0) {
    return `Cannot promote: ${hard.map((b) => b.message).join('; ')}`;
  }
  return blockers.map((b) => b.message).join('; ');
}
